import { AssetTypeCode } from '@atlas/types'
import { z } from 'zod'

const checklistUuidSchema = z.string().uuid()
const checklistTextSchema = z.string().min(1)
const checklistNonNegativeIntegerSchema = z.number().finite().int().min(0)

/** `null`/omitted = unscoped (applies to any asset type); a supplied matcher must be non-empty. */
const templateAssetTypeCodesSchema = z.array(z.enum(AssetTypeCode)).min(1).nullable()

/**
 * Rejects an explicit `undefined` value on any own key and rejects a patch
 * with zero own keys — the same partial-update convention `workflow.schema.ts`
 * uses.
 */
function validateChecklistPatchObject(value: Record<string, unknown>, ctx: z.RefinementCtx): void {
	let definedCount = 0
	for (const key of Object.keys(value)) {
		if (value[key] === undefined) {
			ctx.addIssue({
				code: 'custom',
				message: 'Explicit undefined is not allowed — omit the property instead',
				path: [key],
			})
		} else {
			definedCount += 1
		}
	}
	if (definedCount === 0) {
		ctx.addIssue({ code: 'custom', message: 'At least one field must be supplied' })
	}
}

// ─── checklist_template ──────────────────────────────────────────────────────

export const checklistTemplateCreateSchema = z
	.object({
		code: checklistTextSchema,
		label: checklistTextSchema,
		note: checklistTextSchema.nullable().default(null),
		assetTypeCodes: templateAssetTypeCodesSchema.default(null),
	})
	.strict()

export const checklistTemplateUpdateSchema = z
	.object({
		code: checklistTextSchema.optional(),
		label: checklistTextSchema.optional(),
		note: checklistTextSchema.nullable().optional(),
		assetTypeCodes: templateAssetTypeCodesSchema.optional(),
		isActive: z.boolean().optional(),
	})
	.strict()
	.superRefine(validateChecklistPatchObject)

// ─── checklist_template_item ─────────────────────────────────────────────────

export const checklistTemplateItemCreateSchema = z
	.object({
		templateId: checklistUuidSchema,
		label: checklistTextSchema,
		tag: checklistTextSchema.nullable().default(null),
		required: z.boolean().default(true),
		position: checklistNonNegativeIntegerSchema.default(0),
		autoChecked: z.boolean().default(false),
	})
	.strict()

/** `templateId` is the create-time ownership relationship and is immutable — never accepted here. */
export const checklistTemplateItemUpdateSchema = z
	.object({
		label: checklistTextSchema.optional(),
		tag: checklistTextSchema.nullable().optional(),
		required: z.boolean().optional(),
		position: checklistNonNegativeIntegerSchema.optional(),
		autoChecked: z.boolean().optional(),
		isActive: z.boolean().optional(),
	})
	.strict()
	.superRefine(validateChecklistPatchObject)

// ─── work_order_checklist ────────────────────────────────────────────────────
// No create schema — an instance is created by the resolution/authoring
// service, never directly from an HTTP body (its `items` snapshot is
// computed, not caller-supplied wholesale).

/** Toggle one item's checked value on an existing instance. */
export const checklistItemToggleSchema = z
	.object({
		checked: z.boolean(),
	})
	.strict()

/** Set one change-request item's reviewer verification. */
export const changeRequestItemVerifySchema = z
	.object({
		verified: z.boolean(),
	})
	.strict()

/**
 * Author a dynamic checklist's items, atomically with committing them —
 * every line becomes a required item; blank lines are dropped by the
 * service, not rejected here.
 */
export const dynamicChecklistAuthorSchema = z
	.object({
		itemLabels: z.array(checklistTextSchema).min(1),
	})
	.strict()

export type ChecklistTemplateCreateInput = z.infer<typeof checklistTemplateCreateSchema>
export type ChecklistTemplateUpdateInput = z.infer<typeof checklistTemplateUpdateSchema>
export type ChecklistTemplateItemCreateInput = z.infer<typeof checklistTemplateItemCreateSchema>
export type ChecklistTemplateItemUpdateInput = z.infer<typeof checklistTemplateItemUpdateSchema>
export type ChecklistItemToggleInput = z.infer<typeof checklistItemToggleSchema>
export type ChangeRequestItemVerifyInput = z.infer<typeof changeRequestItemVerifySchema>
export type DynamicChecklistAuthorInput = z.infer<typeof dynamicChecklistAuthorSchema>
